import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MagazineLayout from "@/components/MagazineLayout";
import Seo from "@/components/Seo";
import client from "@/lib/glossyApi";
import { toast } from "sonner";

function BigSubscribe() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const sub = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try { await client.post("/newsletter/subscribe", { email }); toast.success("You're on the list — the next edition will find you."); setEmail(""); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not subscribe."); }
    setBusy(false);
  };
  return (
    <form onSubmit={sub} className="bg-ink text-cream p-8 sm:p-12 grain relative" data-testid="archive-subscribe">
      <div className="relative z-10 max-w-xl">
        <p className="overline text-gloss-gold mb-3">The fortnightly edit</p>
        <h2 className="font-serif text-3xl sm:text-4xl leading-tight">Every edition, straight to your inbox.</h2>
        <div className="flex mt-6">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address"
            className="flex-1 bg-transparent border border-cream/30 border-r-0 px-4 py-3.5 font-sans text-sm focus:outline-none focus:border-gloss-gold transition-colors placeholder:text-cream/40"
            data-testid="archive-subscribe-input" />
          <button type="submit" disabled={busy} className="bg-gloss-gold text-ink px-6 overline hover:bg-cream transition-colors disabled:opacity-60" data-testid="archive-subscribe-btn">
            {busy ? "…" : "Subscribe"}
          </button>
        </div>
      </div>
    </form>
  );
}

export default function NewsletterArchive() {
  const [items, setItems] = useState(null);
  useEffect(() => { client.get("/newsletters").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  return (
    <MagazineLayout>
      <Seo title="The Fortnightly Edit — S/ME Newsletter" description="Browse past editions of the S/ME newsletter — the fortnightly edit of the UK's most stylish SMEs, straight to your inbox." />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <p className="overline text-gloss-gold mb-3">Past editions</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight">The Fortnightly Edit</h1>
        <p className="font-sans text-base text-ink/60 mt-4 max-w-2xl leading-relaxed">Every two weeks, ten members of the magazine land in our readers' inboxes. Here's where the past editions live.</p>

        <div className="mt-12 space-y-px bg-ink/15 border border-ink/15" data-testid="newsletter-archive-list">
          {items === null && <p className="bg-cream px-6 py-10 font-sans text-sm text-ink/40">Loading…</p>}
          {items?.length === 0 && (
            <div className="bg-cream px-6 sm:px-10 py-14 text-center">
              <p className="font-serif text-2xl">The first edition is being written.</p>
              <p className="font-sans text-sm text-ink/50 mt-2">Subscribe below and it will find you the moment it's out.</p>
            </div>
          )}
          {items?.map((n) => (
            <article key={n.issue} className="bg-cream px-6 sm:px-10 py-8" data-testid={`newsletter-edition-${n.issue}`}>
              <p className="overline text-gloss-silver">{String(n.sent_at || n.issue).slice(0, 10)}</p>
              <h2 className="font-serif text-2xl sm:text-3xl mt-1">{n.subject}</h2>
              <p className="font-sans text-sm text-ink/60 mt-2 max-w-2xl leading-relaxed">{n.intro}</p>
              {n.members?.length > 0 && (
                <p className="font-sans text-sm mt-4">
                  <span className="overline text-ink/50 mr-2">Featuring</span>
                  {n.members.map((m, i) => (
                    <span key={m.id}>
                      <Link to={`/member/${m.id}`} className="link-underline text-ink" data-testid={`archive-member-${m.id}`}>{m.business_name}</Link>
                      {i < n.members.length - 1 ? " · " : ""}
                    </span>
                  ))}
                </p>
              )}
            </article>
          ))}
        </div>

        <div className="mt-12"><BigSubscribe /></div>
      </div>
    </MagazineLayout>
  );
}
